/**
 * Placement matrix — the (component × environment) → server table on the
 * application detail page. Rows are components, columns are environments,
 * cells are either the server the component runs on in that env (with the
 * server name + hostname) or an "+ Place" CTA.
 *
 * This is the densest UI in the hosting plugin and the answer to "where does
 * this thing actually run?" — so every cell is one click away from changing
 * the answer. Clicking a placed cell opens an inline server picker; clicking
 * an empty cell does the same against a null starting state.
 */
import { useState, useMemo } from 'react';
import {
  Boxes,
  Plus,
  HardDrive,
  Loader2,
  Trash2,
  Globe,
  ChevronDown,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover.js';
import { api, ApiError, type Component, type Environment, type Placement, type Server } from '@/api/client.js';
import { ROLE_VISUALS } from './CreateComponentDialog.js';
import { cn } from '@/lib/utils.js';

interface Props {
  applicationId: string;
  applicationOrgId: string;
  components: Component[];
  environments: Environment[];
}

export function PlacementsMatrix({
  applicationId,
  applicationOrgId,
  components,
  environments,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const placementsQuery = useQuery({
    queryKey: ['placements', 'app', applicationId],
    queryFn: () => api.placements.forApplication(applicationId),
  });

  // All servers visible to the user — we filter client-side to the ones in
  // the app's org or an ancestor. The server endpoint already returns only
  // visible servers, so the filter is purely for showing the right subset
  // when the user has access to multiple orgs.
  const serversQuery = useQuery({ queryKey: ['servers'], queryFn: () => api.servers.listAll() });
  const reachableServers = useMemo(() => {
    if (!serversQuery.data) return [];
    // Server is reachable if its orgId === applicationOrgId. The ancestor
    // case (MSP's shared server reused by a customer) is covered by the API
    // — we don't have the org path on the SPA, so we trust the API's choice
    // and additionally show same-org servers here. A future round can
    // surface "ancestor" servers by widening the SPA's org data.
    return serversQuery.data.filter((s) => s.orgId === applicationOrgId);
  }, [serversQuery.data, applicationOrgId]);

  const placementsByKey = useMemo(() => {
    const map = new Map<string, Placement>();
    for (const p of placementsQuery.data ?? []) {
      map.set(key(p.componentId, p.environmentId), p);
    }
    return map;
  }, [placementsQuery.data]);

  async function reload() {
    await queryClient.invalidateQueries({ queryKey: ['placements', 'app', applicationId] });
  }

  async function placeOrMove(args: {
    componentId: string;
    environmentId: string;
    serverId: string;
    existing: Placement | undefined;
  }): Promise<void> {
    try {
      if (args.existing) {
        if (args.existing.serverId === args.serverId) return;
        await api.placements.update(args.existing.id, { serverId: args.serverId });
        toast.success('Moved');
      } else {
        await api.placements.create(args.componentId, {
          environmentId: args.environmentId,
          serverId: args.serverId,
        });
        toast.success('Placed');
      }
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save placement');
    }
  }

  async function clearPlacement(placement: Placement) {
    if (!confirm(`Remove ${placement.componentName} from ${placement.environmentName}?`)) return;
    try {
      await api.placements.delete(placement.id);
      toast.success('Removed');
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove placement');
    }
  }

  if (placementsQuery.isLoading || serversQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <Title />
        </CardHeader>
        <CardContent className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (components.length === 0 || environments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Title />
        </CardHeader>
        <CardContent>
          <p className="rounded-md border border-dashed border-border/70 bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
            {components.length === 0 && environments.length === 0
              ? 'Add at least one component and one environment to start placing.'
              : components.length === 0
                ? 'Add a component above to start placing.'
                : 'Add an environment above to start placing.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (reachableServers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Title />
        </CardHeader>
        <CardContent>
          <p className="rounded-md border border-dashed border-border/70 bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
            No servers in this organisation yet. Create one on the Servers page first.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Title />
        <CardDescription>
          Which server each component runs on, per environment. Click a cell to assign or move.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="border-b border-border/70 py-2 pr-3 text-left font-medium">
                  Component
                </th>
                {environments.map((env) => (
                  <th key={env.id} className="border-b border-border/70 py-2 px-3 text-left font-medium">
                    <div className="flex items-center gap-1.5">
                      <Layers className="h-3 w-3" />
                      <span>{env.name}</span>
                      <span className="font-mono text-[10px] opacity-60">/{env.slug}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {components.map((cmp) => {
                const visual = ROLE_VISUALS[cmp.role] ?? ROLE_VISUALS.other!;
                const Icon = visual.icon;
                return (
                  <tr key={cmp.id} className="align-top">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="leading-tight">
                          <div className="font-medium">{cmp.name}</div>
                          <div className="text-[10px] text-muted-foreground">{cmp.role}</div>
                        </div>
                      </div>
                    </td>
                    {environments.map((env) => {
                      const existing = placementsByKey.get(key(cmp.id, env.id));
                      const onClear = existing
                        ? () => {
                            void clearPlacement(existing);
                          }
                        : undefined;
                      return (
                        <td key={env.id} className="py-1.5 px-3">
                          <PlacementCell
                            placement={existing}
                            servers={reachableServers}
                            onPick={(serverId) =>
                              placeOrMove({
                                componentId: cmp.id,
                                environmentId: env.id,
                                serverId,
                                existing,
                              })
                            }
                            {...(onClear ? { onClear } : {})}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Title() {
  return (
    <div className="flex items-center justify-between gap-2">
      <CardTitle className="flex items-center gap-2">
        <Boxes className="h-4 w-4" />
        Placements
      </CardTitle>
    </div>
  );
}

function PlacementCell({
  placement,
  servers,
  onPick,
  onClear,
}: {
  placement: Placement | undefined;
  servers: Server[];
  onPick: (serverId: string) => Promise<void> | void;
  onClear?: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {placement ? (
          <button
            type="button"
            className="group inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs hover:border-primary/60"
          >
            <HardDrive className="h-3 w-3 text-muted-foreground group-hover:text-primary" />
            <span className="truncate font-medium">{placement.serverName}</span>
            {placement.serverHostname && (
              <span className="hidden truncate text-muted-foreground sm:inline">
                <span className="px-1">·</span>
                <span className="font-mono">{placement.serverHostname}</span>
              </span>
            )}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </button>
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary"
          >
            <Plus className="h-3 w-3" />
            Place
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
          {placement ? 'Move to server' : 'Pick a server'}
        </div>
        <ul className="max-h-72 overflow-y-auto py-1">
          {servers.map((s) => {
            const isCurrent = placement?.serverId === s.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={async () => {
                    setOpen(false);
                    await onPick(s.id);
                  }}
                  className={cn(
                    'flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent/60',
                    isCurrent && 'bg-primary/10',
                  )}
                >
                  <HardDrive className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{s.name}</span>
                    {s.hostname && (
                      <span className="truncate text-[11px] text-muted-foreground">
                        <Globe className="mr-1 inline h-2.5 w-2.5" />
                        <span className="font-mono">{s.hostname}</span>
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {onClear && (
          <div className="border-t border-border px-2 py-1.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onClear();
              }}
              className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3" />
              Remove placement
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function key(componentId: string, environmentId: string): string {
  return `${componentId}::${environmentId}`;
}

export type { Placement };
